import type { TenantModules } from '~~/shared/schemas/tenantModules'
import type { AuthUserDto } from '~~/shared/schemas/auth'
import type { $Fetch } from 'ofetch'

interface AuthTenant {
  id: string
  key: string
  name: string
  status: 'active' | 'suspended' | 'archived'
  system: boolean
}

interface AuthIdentity {
  id: string
  tenantId: string
  loginId: string
  username: string
  email: string
}

interface SessionResponse {
  capabilities?: { commercialLicensing: boolean }
  modules?: TenantModules
  authenticated: true
  user: AuthUserDto
  account: { id: string }
  identity: AuthIdentity | null
  tenant: AuthTenant
  accessibleTenantCount: number
  tenantSelectionRequired: boolean
  rememberedTenantId: string | null
  mode: 'normal' | 'platform-administration'
  platformPermissions: string[]
}

export function useAuth() {
  const analytics = useAnalytics()
  const requestFetch = useRequestFetch() as $Fetch
  const nuxtApp = useNuxtApp()
  const capabilities = useState('morning-auth-capabilities', () => ({ commercialLicensing: false }))
  const modules = useState<TenantModules>('morning-tenant-modules', () => ({}))
  const user = useState<AuthUserDto | null>('morning-auth-user', () => null)
  const account = useState<{ id: string } | null>('morning-auth-account', () => null)
  const loaded = useState('morning-auth-loaded', () => false)
  const pending = useState('morning-auth-pending', () => false)
  const contextUnconfirmed = useState('morning-auth-context-unconfirmed', () => false)
  const switching = useState('morning-auth-switching', () => false)
  const generation = useState('morning-auth-generation', () => 0)
  const tenant = useState<AuthTenant | null>('morning-auth-tenant', () => null)
  const identity = useState<AuthIdentity | null>('morning-auth-identity', () => null)
  const accessibleTenantCount = useState('morning-auth-accessible-tenant-count', () => 0)
  const tenantSelectionRequired = useState('morning-auth-tenant-selection-required', () => false)
  const mode = useState<'normal' | 'platform-administration'>('morning-auth-mode', () => 'normal')
  const platformPermissions = useState<string[]>('morning-auth-platform-permissions', () => [])

  async function refresh() {
    const requestGeneration = ++generation.value
    pending.value = true
    try {
      const response = await requestFetch<SessionResponse>('/api/auth/session')
      if (requestGeneration !== generation.value) return user.value
      capabilities.value = {
        commercialLicensing: response.capabilities?.commercialLicensing === true,
      }
      modules.value = response.modules ?? {}
      user.value = response.user
      account.value = response.account
      tenant.value = response.tenant
      identity.value = response.identity
      accessibleTenantCount.value = response.accessibleTenantCount
      tenantSelectionRequired.value = response.tenantSelectionRequired
      mode.value = response.mode
      platformPermissions.value = response.platformPermissions
      contextUnconfirmed.value = false
    } catch {
      if (requestGeneration !== generation.value) return user.value
      capabilities.value = { commercialLicensing: false }
      modules.value = {}
      user.value = null
      account.value = null
      tenant.value = null
      identity.value = null
      accessibleTenantCount.value = 0
      tenantSelectionRequired.value = false
      mode.value = 'normal'
      platformPermissions.value = []
    } finally {
      if (requestGeneration === generation.value) {
        loaded.value = true
        pending.value = false
      }
    }
    return user.value
  }

  async function switchTenant(tenantId: string, remember: boolean) {
    if (switching.value) throw new Error('Un changement d’entreprise est déjà en cours.')
    const previousCapabilities = capabilities.value
    capabilities.value = { commercialLicensing: false }
    switching.value = true
    generation.value++
    let accepted = false
    try {
      await requestFetch('/api/auth/tenant-switch', {
        method: 'POST',
        body: { tenantId, remember },
      })
      accepted = true
      contextUnconfirmed.value = true
      if (import.meta.client)
        nuxtApp.runWithContext(() =>
          clearNuxtData((key) => !key.startsWith('account:') && key !== 'auth-bootstrap'),
        )
      await refresh()
      if (!user.value || tenant.value?.id !== tenantId) {
        contextUnconfirmed.value = true
        throw new Error('Impossible de confirmer le contexte de la nouvelle entreprise.')
      }
      // Les useFetch des applications, services et écrans d'administration sont tenantés côté
      // serveur, mais leur cache client doit aussi être invalidé lors du changement de contexte.
      if (import.meta.client) {
        await nuxtApp.runWithContext(async () => {
          await refreshNuxtData()
        })
      }
      analytics.trackEvent('tenant_action', { action: 'switch', result: 'success' })
    } catch (cause) {
      if (!accepted) {
        const status =
          cause && typeof cause === 'object'
            ? Number(
                ('statusCode' in cause && cause.statusCode) || ('status' in cause && cause.status),
              )
            : 0
        if ([400, 403, 404, 409, 422, 429].includes(status))
          capabilities.value = previousCapabilities
        else contextUnconfirmed.value = true
      }
      analytics.trackEvent('operation_failure', {
        domain: 'tenant',
        action: 'switch',
        status_category: analyticsStatusCategory(cause),
      })
      throw cause
    } finally {
      switching.value = false
      pending.value = false
    }
  }

  return {
    capabilities,
    modules,
    user,
    account,
    tenant,
    identity,
    accessibleTenantCount,
    tenantSelectionRequired,
    mode,
    platformPermissions,
    authenticated: computed(() => Boolean(user.value)),
    loaded,
    pending,
    switching,
    contextUnconfirmed,
    generation,
    contextKey: computed(
      () =>
        `${account.value?.id ?? ''}:${identity.value?.id ?? ''}:${tenant.value?.id ?? ''}:${mode.value}:${generation.value}`,
    ),
    refresh,
    switchTenant,
  }
}
